'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import s from './Modal.module.scss';
import Icon from '../Icon';
import Button from '../Button/Button';
import classNames from 'classnames';

const Modal = ({
    title = '',
    children,
    buttonContent,
    isCustomButton = true,
    customButtonModifier = 'Secondary',
    showButtonArrow = false,
    bottomButtonContent,
    bottomButtonOnClick,
    buttonTitle,
    modifiers = [],
    isOpen = false,
    disableOutsideClick = false,
    onClose,
    disableOpen = false,
    isFullWidth = false,
}) => {
    const [showModal, setShowModal] = useState(isOpen);
    const modalRef = useRef(null);
    const titleId = useId();

    const modifierClasses = classNames(
        s.Root,
        modifiers.map((modifier) => s[modifier]),
        [{ [s.IsOpen]: showModal }]
    );

    const contentClasses = classNames(
        s.Content,
        modifiers.map((modifier) => s[`Content--${modifier}`])
    );

    const handleOverlayClick = (event) => {
        if (
            !disableOutsideClick &&
            modalRef.current &&
            !modalRef.current.contains(event.target)
        ) {
            setShowModal(false);
            if (onClose) onClose();
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Escape') {
            setShowModal(false);
            if (onClose) onClose();
        }
    };

    const handleBottomButtonClick = () => {
        if (bottomButtonOnClick) {
            bottomButtonOnClick();
        }
        setShowModal(false);
        if (onClose) onClose();
    };

    useEffect(() => {
        if (showModal) {
            document.addEventListener('keydown', handleKeyPress);
        } else {
            document.removeEventListener('keydown', handleKeyPress);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [showModal]);

    useEffect(() => {
        if (isOpen !== showModal) {
            setShowModal(isOpen);
        }
    }, [isOpen]);

    const closeModal = () => {
        setShowModal(false);
        if (onClose) onClose();
    };

    const modalContent = showModal && (
        <div className={s.Overlay} onClick={handleOverlayClick}>
            <div
                className={contentClasses}
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}>
                <div className={s.Header}>
                    <h2 id={titleId} className={s.Title}>
                        {title}
                    </h2>
                    <button
                        className={s.CloseButton}
                        aria-label="Close"
                        onClick={closeModal}>
                        <Icon name="CloseRound" />
                    </button>
                </div>
                <div className={s.Body}>
                    {typeof children === 'function'
                        ? children({ close: closeModal })
                        : children}
                </div>
                {!!bottomButtonContent && (
                    <button
                        type="button"
                        onClick={handleBottomButtonClick}
                        className={s.BottomButton}>
                        {bottomButtonContent}
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className={modifierClasses}>
            {isCustomButton && (
                <button
                    className={s.ToggleButton}
                    onClick={disableOpen ? null : () => setShowModal(true)}>
                    {buttonContent}
                </button>
            )}
            {!isCustomButton && (
                <Button
                    title={buttonTitle}
                    modifier={customButtonModifier}
                    icon={showButtonArrow ? 'Arrow' : null}
                    size="Large"
                    isFullWidth={isFullWidth}
                    onClick={() => setShowModal(true)}
                />
            )}

            {showModal && createPortal(modalContent, document.body)}
        </div>
    );
};

Modal.propTypes = {
    title: PropTypes.string,
    buttonText: PropTypes.string,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    // A function child receives { close } so content can dismiss the modal,
    // which owns the open state.
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default Modal;
